<template>
  <!-- Indicadores agregados por grupo de proyecto (sustituye la "línea de negocio" del demo). Clic = filtrar. -->
  <summary-card :title="$t('prm_portfolio_groups_title')" :subtitle="subtitle" :mode="mode">
    <div class="atable-wrap">
      <table class="atable">
        <thead>
          <tr>
            <th v-for="c in columns" :key="c.key" :class="c.align">
              <span :style="c.sem ? { color: SEM[c.sem] } : null">{{ $t(c.label) }}</span>
              <source-chip v-if="mode && c.source" :source="c.source" :tip-override="c.tip ? $t(c.tip) : ''"></source-chip>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in groups" :key="a.group" class="arow" :class="{ active: a.group === activeGroup }"
            role="button" tabindex="0" @click="$emit('toggle-group', a.group)" @keydown.enter="$emit('toggle-group', a.group)"
            @keydown.space.prevent="$emit('toggle-group', a.group)" v-tip="tipFor(a)">
            <td class="left aname"><group-tag :group="a.group" :color="a.color"></group-tag></td>
            <td class="right">{{ a.numProjects }}</td>
            <td class="right">{{ money(a.totalSale) }}</td>
            <td class="right">{{ money(a.backlog) }}</td>
            <td class="right">{{ money(a.changeOrders) }}</td>
            <td class="right">{{ pct(a.avgCommercialMargin) }}</td>
            <td class="right">{{ pct(a.avgActualMargin) }}</td>
            <td class="right" :style="{ color: a.marginDev < 0 ? SEM.red : SEM.green }">{{ moneySigned(a.marginDev) }}</td>
            <td class="right">{{ pct(a.avgPoc) }}</td>
            <td class="center"><span class="cnt" :style="cntStyle('green', a.green)">{{ a.green }}</span></td>
            <td class="center"><span class="cnt" :style="cntStyle('amber', a.amber)">{{ a.amber }}</span></td>
            <td class="center"><span class="cnt" :style="cntStyle('red', a.red)">{{ a.red }}</span></td>
          </tr>
          <tr v-if="!groups.length">
            <td :colspan="columns.length" class="empty">{{ $t('prm_portfolio_no_projects') }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </summary-card>
</template>

<script>
  import SummaryCard from '../summary/summary-card.vue'
  import SourceChip from '../summary/source-chip.vue'
  import GroupTag from './group-tag.vue'
  import { tip } from '../shared/tip'
  import { fmtEur, fmtPct, fmtEurSigned } from '../shared/format'
  import { SEM } from '../dashboard/dashboard-model'

  const COLUMNS = [
    { key: 'group', label: 'prm_portfolio_col_group', align: 'left', source: 'admin' },
    { key: 'num', label: 'prm_portfolio_col_num_projects', align: 'right', source: 'calc', tip: 'prm_portfolio_tip_num_projects' },
    { key: 'sale', label: 'prm_portfolio_col_total_sale', align: 'right', source: 'prm' },
    { key: 'backlog', label: 'prm_portfolio_col_backlog', align: 'right', source: 'calc', tip: 'prm_portfolio_tip_group_backlog' },
    { key: 'co', label: 'prm_portfolio_col_change_orders', align: 'right', source: 'prm' },
    { key: 'mcom', label: 'prm_portfolio_col_avg_commercial_margin', align: 'right', source: 'calc', tip: 'prm_portfolio_tip_avg_commercial' },
    { key: 'mproj', label: 'prm_portfolio_col_avg_project_margin', align: 'right', source: 'calc', tip: 'prm_portfolio_tip_avg_project' },
    { key: 'mdev', label: 'prm_portfolio_col_margin_dev', align: 'right', source: 'calc', tip: 'prm_portfolio_tip_group_margin_dev' },
    { key: 'poc', label: 'prm_portfolio_col_avg_poc', align: 'right', source: 'calc', tip: 'prm_portfolio_tip_avg_poc' },
    { key: 'green', label: 'prm_portfolio_col_green', align: 'center', sem: 'green', source: 'calc', tip: 'prm_portfolio_tip_group_counts' },
    { key: 'amber', label: 'prm_portfolio_col_amber', align: 'center', sem: 'amber', source: 'calc', tip: 'prm_portfolio_tip_group_counts' },
    { key: 'red', label: 'prm_portfolio_col_red', align: 'center', sem: 'red', source: 'calc', tip: 'prm_portfolio_tip_group_counts' },
  ]

  export default {
    name: 'PrmPortfolioGroupAggregates',

    components: { SummaryCard, SourceChip, GroupTag },

    directives: { tip },

    props: {
      groups: { type: Array, default: () => [] },     // salida de groupAggregates()
      activeGroup: { type: String, default: null },
      subtitle: { type: String, default: '' },
      mode: { type: Boolean, default: false },
    },

    data: () => ({ columns: COLUMNS, SEM }),

    methods: {
      money (v) { return fmtEur(v) },
      moneySigned (v) { return fmtEurSigned(v) },
      pct (v) { return fmtPct(v) },
      // Recuento como número coloreado por semáforo (sin círculo); gris tenue si es cero.
      cntStyle (state, n) {
        return { color: n ? SEM[state] : '#c0c6cf' }
      },
      tipFor (a) {
        return a.group === this.activeGroup ? this.$t('prm_portfolio_group_clear') : this.$t('prm_portfolio_group_filter')
      },
    },
  }
</script>

<style lang="scss" scoped>
  .atable-wrap { overflow-x: auto; }
  .atable { width: 100%; min-width: 1000px; border-collapse: collapse; }

  .atable thead th {
    background: #3d6b9a; color: #fff; font-size: 9px; font-weight: 700; text-transform: uppercase;
    letter-spacing: .03em; padding: 5px 8px; white-space: nowrap;
  }
  .atable thead th.left { text-align: left; }
  .atable thead th.right { text-align: right; }
  .atable thead th.center { text-align: center; }
  .atable thead th span { color: #fff; }

  .atable tbody td {
    border-bottom: 1px solid #eef0f3; padding: 5px 8px; font-size: 11px; color: #1e3046; white-space: nowrap;
  }
  .atable tbody td.left { text-align: left; }
  .atable tbody td.right { text-align: right; font-variant-numeric: tabular-nums; }
  .atable tbody td.center { text-align: center; }
  .atable tbody tr:nth-child(even) td { background: #f9fafb; }
  .arow { cursor: pointer; }
  .arow:hover td { background: #eef2f7; }
  // Fila activa (filtro): fondo azul suave; el acento a la izquierda solo en la primera celda (no en cada una).
  .arow.active td { background: #eaf1fb; }
  .arow.active td:first-child { box-shadow: inset 3px 0 0 0 #3a6bc8; }
  .arow:focus { outline: none; }
  .aname { font-weight: 600; }

  .cnt { font-weight: 700; font-variant-numeric: tabular-nums; }
  .empty { text-align: center; color: #9aa4b2; padding: 18px; font-size: 11px; }
</style>
